import { Paperclip } from "lucide-react";
import { Badge, Button } from "@/design-system/components/ui";
import { useDateFormatter } from "@/utils/datetime";
import { relativeTime } from "@/components/ux/relativeTime";
import { BrokerRecordLink } from "@/components/crosslinks/BrokerCrossLinks";
import { VendorRecordLink } from "@/components/crosslinks/VendorCrossLinks";
import { fallbackOwnerLabel, type OwnerLabel } from "@/api/ownerLabels";
import type {
  AccountSubmissionSummaryDto,
  PortalAccountActivityDto,
  PortalAccountAttachmentDto,
  PortalAccountDetailDto,
} from "@/api/portalAccounts";

// Disable / Re-enable / Archive — humanise the AuditAction string for the
// activity rail (mirrors the inline ActivityLogSection helper).
function activityActionLabel(action: string): string {
  switch (action) {
    case "StatusChange":
      return "Status change";
    case "Archive":
      return "Archived";
    case "Unarchive":
      return "Unarchived";
    default:
      return action;
  }
}

interface PortalAccountOverviewTabProps {
  account: PortalAccountDetailDto;
  summary?: AccountSubmissionSummaryDto;
  activity: PortalAccountActivityDto[];
  activityLoading: boolean;
  ownerLookup: (ref: { type: string; id: string }) => OwnerLabel | null;
  onEditAttachments: () => void;
  onSeeAllActivity: () => void;
}

/**
 * Layout Standard §4.1 — Overview tab for the Portal Account detail page.
 *
 * Two-column grid: account summary card on the left (md:col-span-2) with
 * party type, contact info, key counts from the submission summary, linked
 * Broker/Vendor record links, and a compact attached-records list with an
 * "Edit attachments" affordance. Right rail (md:col-span-1) is a "Recent
 * activity" preview sourced from the existing portalAccountsApi.getActivity
 * endpoint (kept here rather than swapping to /api/activity/by-owner so the
 * portal_user owner type doesn't need to be wired separately).
 */
export function PortalAccountOverviewTab({
  account,
  summary,
  activity,
  activityLoading,
  ownerLookup,
  onEditAttachments,
  onSeeAllActivity,
}: PortalAccountOverviewTabProps) {
  const { date } = useDateFormatter();
  const attachments = account.attachments ?? [];
  const previewActivity = activity.slice(0, 5);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-space-4">
      <div className="bg-white-pure border border-border-light rounded-lg p-space-4 md:col-span-2 flex flex-col gap-space-4">
        <section>
          <h3 className="mb-space-3 font-display font-bold text-small uppercase tracking-wider text-gold-shadow">
            Account Summary
          </h3>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-space-2 text-small">
            <dt className="text-gray-mid">Party type</dt>
            <dd>
              <Badge status="info">{account.accountType}</Badge>{" "}
              <span className="text-caption text-gray-mid">({account.kind})</span>
            </dd>
            <dt className="text-gray-mid">Full name</dt>
            <dd>{account.fullName || "—"}</dd>
            <dt className="text-gray-mid">Email</dt>
            <dd className="break-all">{account.email}</dd>
            <dt className="text-gray-mid">Email confirmed</dt>
            <dd>
              {account.emailConfirmed ? (
                <Badge status="finalized">Confirmed</Badge>
              ) : (
                <Badge status="review">Pending</Badge>
              )}
            </dd>
            <dt className="text-gray-mid">Created</dt>
            <dd>{date(account.createdAt)}</dd>
            <dt className="text-gray-mid">Last login</dt>
            <dd>
              {account.lastLoginUtc ? (
                date(account.lastLoginUtc)
              ) : (
                <span className="text-gray-mid italic">Never logged in</span>
              )}
            </dd>
          </dl>
        </section>

        {summary && (
          <section className="border-t border-border-light pt-space-3">
            <h4 className="mb-space-2 text-caption font-semibold uppercase tracking-wide text-gold-shadow">
              Workload
            </h4>
            <dl className="grid grid-cols-2 md:grid-cols-3 gap-space-2 text-small">
              <dt className="text-gray-mid">Packages</dt>
              <dd>{summary.packageCount}</dd>
              <dt className="text-gray-mid">Items</dt>
              <dd>{summary.totalItemCount}</dd>
              <dt className="text-gray-mid">Pending</dt>
              <dd>{summary.totalPendingCount}</dd>
              <dt className="text-gray-mid">Awaiting review</dt>
              <dd>{summary.awaitingReviewCount}</dd>
              <dt className="text-gray-mid">Overdue</dt>
              <dd>
                {summary.overdueCount > 0 ? (
                  <span className="font-semibold text-status-error">
                    {summary.overdueCount}
                  </span>
                ) : (
                  summary.overdueCount
                )}
              </dd>
              <dt className="text-gray-mid">Pending recipient action</dt>
              <dd>{summary.pendingRecipientActionCount}</dd>
            </dl>
          </section>
        )}

        {(account.kind === "Broker" ||
          account.accountType === "Vendor" ||
          account.linkedBrokerId ||
          account.linkedVendorId) && (
          <section className="border-t border-border-light pt-space-3 flex flex-col gap-space-2">
            <h4 className="text-caption font-semibold uppercase tracking-wide text-gold-shadow">
              Linked record
            </h4>
            <div className="flex flex-wrap items-center gap-space-3 text-small">
              {account.kind === "Broker" &&
                (account.linkedBrokerId ? (
                  <BrokerRecordLink
                    brokerId={account.linkedBrokerId}
                    className="text-onyx hover:text-gold-shadow font-semibold"
                  />
                ) : (
                  <span className="text-gray-mid italic">No broker record linked</span>
                ))}
              {account.accountType === "Vendor" &&
                (account.linkedVendorId ? (
                  <VendorRecordLink
                    vendorId={account.linkedVendorId}
                    label={
                      account.linkedVendorName
                        ? `View vendor record (${account.linkedVendorName})`
                        : undefined
                    }
                    className="text-onyx hover:text-gold-shadow font-semibold"
                  />
                ) : (
                  <span className="text-gray-mid italic">No vendor record linked</span>
                ))}
            </div>
          </section>
        )}

        <section className="border-t border-border-light pt-space-3 flex flex-col gap-space-2">
          <div className="flex items-center justify-between gap-space-3">
            <h4 className="text-caption font-semibold uppercase tracking-wide text-gold-shadow">
              Attached owners ({attachments.length})
            </h4>
            <Button
              size="sm"
              variant="text"
              type="button"
              onClick={onEditAttachments}
              data-testid="portal-account-edit-attachments"
            >
              <Paperclip size={14} strokeWidth={1.5} aria-hidden /> Edit attachments
            </Button>
          </div>
          {attachments.length === 0 ? (
            <p className="text-small text-gray-mid m-0">
              No internal records attached yet.
            </p>
          ) : (
            <ul className="flex flex-col gap-space-2 m-0 p-0 list-none">
              {attachments.map((a: PortalAccountAttachmentDto) => {
                const ref = { type: a.entityType, id: a.entityId };
                const lbl = ownerLookup(ref);
                const pretty = a.entityType === "LegalEntity" ? "Entity" : a.entityType;
                return (
                  <li
                    key={a.id}
                    className="flex flex-col gap-space-1 rounded border border-border-light px-space-3 py-space-2"
                  >
                    <div className="text-small">
                      <span className="text-caption uppercase text-gray-mid mr-space-1">
                        {pretty}
                      </span>
                      {lbl ? lbl.label : fallbackOwnerLabel(ref)}
                    </div>
                    {lbl?.secondary && (
                      <div className="text-caption text-gray-mid">{lbl.secondary}</div>
                    )}
                    {a.notes && (
                      <div className="text-caption text-gray-mid">{a.notes}</div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>

      <section
        aria-label="Recent activity"
        className="border border-border-light rounded-md bg-white-pure p-space-3 min-w-0"
        data-testid="portal-account-overview-activity-preview"
      >
        <div className="flex items-center justify-between mb-space-2">
          <h3 className="font-display font-bold text-h4 text-onyx m-0">
            Recent activity
          </h3>
          <button
            type="button"
            onClick={onSeeAllActivity}
            className="text-caption text-gold-shadow hover:underline bg-transparent border-0 p-0 cursor-pointer"
          >
            See all activity →
          </button>
        </div>

        {activityLoading && (
          <p className="text-small text-gray-mid m-0">Loading…</p>
        )}

        {!activityLoading && previewActivity.length === 0 && (
          <p className="text-small text-gray-mid m-0">No activity yet.</p>
        )}

        {previewActivity.length > 0 && (
          <ul className="m-0 p-0 list-none divide-y divide-border-light">
            {previewActivity.map((event) => (
              <li key={event.id} className="py-space-2 first:pt-0 last:pb-0">
                <div className="flex items-baseline justify-between gap-space-2 min-w-0">
                  <span
                    className="text-small text-onyx font-semibold truncate min-w-0"
                    title={activityActionLabel(event.action)}
                  >
                    {activityActionLabel(event.action)}
                  </span>
                  <span className="text-label text-gray-mid shrink-0">
                    {relativeTime(event.occurredAtUtc)}
                  </span>
                </div>
                {event.description && (
                  <div className="text-small text-onyx mt-space-1 break-words">
                    {event.description}
                  </div>
                )}
                <div className="text-label text-gray-mid mt-space-1 break-words">
                  {event.actorDisplayName ?? "Unknown user"}
                  {event.actorRoleCode ? ` · ${event.actorRoleCode}` : ""}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
